import { IFramework } from '@/framework';
import { ILayerInfo, ILayerProps, ResizeOperateType, ScaleOperateType } from '@/version-types';

/**
 * 获取当前操作的图层
 * @param framework - 框架
 * @returns
 */
export function getCurrentOperateLayer(framework: IFramework) {
    const activeInfo = framework.operate.getActiveInfo();

    let layer: ILayerProps | null = null;
    if (activeInfo?.info?.pickId) {
        layer = framework.getLayer(activeInfo.info.pickId);
        if (framework.checkDisableChildBaseOperate(layer)) {
            layer = null;
        }
    }
    if (!layer) {
        layer = framework.operate.getActiveLayer();
    }
    return layer;
}

/**
 * 向量的旋转
 * x1 = x * cos(θ) - y * sin(θ)
 * y1 = x * sin(θ) + y * cos(θ)
 */

/**
 * 获取水平方向的旋转后的向量
 * @param angle - 旋转角度
 * @returns
 */
export const getDirectionXByAngle = (angle: number) => {
    const arc = ((360 - angle) / 180) * Math.PI;

    // 水平方向: 为 A(0, 0)B(1, 0) 的向量
    let x = Math.cos(arc);

    // 兼容三角函数精度问题
    if (Math.abs(x) < 1e-10) {
        x = 0;
    }

    return Math.sign(x);
};

/**
 * 获取垂直方向的旋转后的向量
 * @param angle - 旋转角度
 * @returns
 */
export const getDirectionYByAngle = (angle: number) => {
    const arc = ((360 - angle) / 180) * Math.PI;

    // 垂直方向: 为 A(0, 0)B(0, -1) 的向量
    let y = Math.sin(arc);

    // 兼容三角函数精度问题
    if (Math.abs(y) < 1e-10) {
        y = 0;
    }

    return Math.sign(y);
};

/**
 * 获取操作类型对应的向量
 */
const operateTypeMap = {
    [ResizeOperateType.LC]: [-1, 0],
    [ResizeOperateType.RC]: [1, 0],
    [ResizeOperateType.TC]: [0, 1],
    [ResizeOperateType.BC]: [0, -1],
    [ScaleOperateType.LT]: [-1, 1],
    [ScaleOperateType.LB]: [-1, -1],
    [ScaleOperateType.RT]: [1, 1],
    [ScaleOperateType.RB]: [1, -1],
};

// 获取旋转后的向量方向
export const getDirectionVector = (operateType: ResizeOperateType | ScaleOperateType, angle: number) => {
    const [x1, y1] = operateTypeMap[operateType];
    const arc = ((360 - angle) / 180) * Math.PI;
    let x = x1 * Math.cos(arc) - y1 * Math.sin(arc);
    // 网页坐标跟笛卡尔坐标在y轴方向上相反
    let y = -(x1 * Math.sin(arc) + y1 * Math.cos(arc));

    // 兼容三角函数精度问题
    if (Math.abs(x) < 1e-10) x = 0;
    if (Math.abs(y) < 1e-10) y = 0;

    return {
        x,
        y,
    };
};

/**
 * 检测图层是否需要参与当前图层的智能对齐
 * @param currentLayerRect - 当前图层
 * @param checkLayerRect - 需要检测的图层
 * @param directionVector - 方向向量
 */
export const checkLayerNeedToAlign = (
    currentLayerRect: ILayerInfo,
    checkLayerRect: ILayerInfo,
    directionVector: { x: number; y: number },
) => {
    const itemLeft = checkLayerRect.left;
    const itemRight = checkLayerRect.left + checkLayerRect.width;
    const itemTop = checkLayerRect.top;
    const itemBottom = checkLayerRect.top + checkLayerRect.height;
    const layerLeft = currentLayerRect.left;
    const layerRight = currentLayerRect.left + currentLayerRect.width;
    const layerTop = currentLayerRect.top;
    const layerBottom = currentLayerRect.top + currentLayerRect.height;

    let verticalCanAdd = false;
    let horizontalCanAdd = false;

    // 水平正向
    if (directionVector.x > 0) {
        // 超过图层左侧的图形
        if (itemRight > layerLeft) {
            horizontalCanAdd = true;
        }
    }
    // 水平反向
    else if (directionVector.x < 0) {
        // 超过图层右侧的图形
        if (itemLeft < layerRight) {
            horizontalCanAdd = true;
        }
    }
    // === 0 垂直方向, 左右侧的都需要加入
    else {
        horizontalCanAdd = true;
    }

    // 垂直正向
    if (directionVector.y > 0) {
        // 超过图层下侧的图形
        if (itemBottom > layerTop) {
            verticalCanAdd = true;
        }
    }
    // 垂直反向
    else if (directionVector.y < 0) {
        // 超过图层上侧的图形
        if (itemTop < layerBottom) {
            verticalCanAdd = true;
        }
    }
    // === 0 水平方向, 上下侧的都需要加入
    else {
        verticalCanAdd = true;
    }

    return horizontalCanAdd && verticalCanAdd;
};

/**
 * 获取吸附偏移量
 * @param rotate - 旋转角度
 * @param additionalRectSize - 新增的 Rect 尺寸
 * @param isChangeHeight - 是否改变高度
 * @param isHorizontal - 是否水平吸附
 */
export function getAbsorbOffset({
    rotate,
    /**
     * Rect 尺寸增量
     */
    additionalRectSize,
    isChangeHeight,
    isHorizontal,
}: {
    rotate: number;
    additionalRectSize: number;
    isChangeHeight: boolean;
    isHorizontal: boolean;
}) {
    // 没有旋转, 直接返回对应的宽度或高度
    if (rotate === 0) {
        return additionalRectSize;
    }

    const arc = ((360 - rotate) / 180) * Math.PI;
    const cosArc = Math.abs(Math.cos(arc));
    const sinArc = Math.abs(Math.sin(arc));

    // 水平方向的吸附
    if (isHorizontal) {
        if (isChangeHeight) {
            // 新增的图层高度
            return Math.round(additionalRectSize / cosArc);
        } else {
            // 新增的图层宽度
            return Math.round(additionalRectSize / sinArc);
        }
    }
    // 垂直方向的吸附
    else {
        if (isChangeHeight) {
            // 新增的图层高度
            return Math.round(additionalRectSize / sinArc);
        } else {
            // 新增的图层宽度
            return Math.round(additionalRectSize / cosArc);
        }
    }
}

/**
 * 获取吸附偏移量
 * @param rotate - 旋转角度
 * @param additionalRectSize - 新增的 Rect 尺寸
 * @param isHorizontal - 是否水平吸附
 * @param mouseOffsetX - 宽度变化
 * @param mouseOffsetY - 高度变化
 * @param oldLayer - 旧图层
 * @param oldLayerRect - 旧图层的外框
 */
export function getScaleAbsorbOffset({
    rotate,
    /**
     * Rect 尺寸增量
     */
    additionalRectSize,
    isHorizontal,
    mouseOffsetX,
    mouseOffsetY,
    oldLayer,
    oldLayerRect,
}: {
    rotate: number;
    additionalRectSize: number;
    isHorizontal: boolean;
    mouseOffsetX: number;
    mouseOffsetY: number;
    oldLayer: ILayerProps;
    oldLayerRect: ILayerInfo;
}) {
    // 没有旋转, 直接返回对应的宽高缩放
    if (rotate === 0) {
        const scale = isHorizontal ? mouseOffsetY / oldLayerRect.height : mouseOffsetX / oldLayerRect.width;
        return {
            x: Math.round(oldLayerRect.width * scale),
            y: Math.round(oldLayerRect.height * scale),
        };
    }

    // 原始图层的包围盒尺寸
    const oldRectSize = isHorizontal ? oldLayerRect.height : oldLayerRect.width;
    // 图层的宽高跟包围盒的比例是固定的
    const widthScale = oldLayer.width / oldRectSize;
    const heightScale = oldLayer.height / oldRectSize;

    const newRectSize = oldRectSize + additionalRectSize;
    // 缩放后的宽度/高度
    const newWidth = widthScale * newRectSize;
    const newHeight = heightScale * newRectSize;

    return {
        x: Math.round(newWidth - oldLayer.width),
        y: Math.round(newHeight - oldLayer.height),
    };
}
